import Head from 'next/head';

import { InfoText, Product, Review } from '@virginexperiencedays/products';

import config from '../../../SEO/structured_data.config.json';
import { imageObject } from '../../../SEO/utils/imageObject';
import breadcrumbs from './Breadcrumbs';
import itemPage from './ItemPage';
import productData from './Product';
import faqs from './FAQ';
import { filterObjectByKey } from '../../../SEO/utils/filterObjectByKey';

type StructuredDataProps = {
  className?: string;
  product: Product;
  reviews?: Review[];
  pagePathOverride?: string;
  hideReviews?: boolean;
  hideOffers?: boolean;
};

const StructuredData = ({
  product,
  reviews,
  pagePathOverride,
  hideReviews,
  hideOffers,
}: StructuredDataProps) => {
  const {
    info: { texts = null },
  } = product;

  return (
    <Head>
      <script
        key="product-structured-data"
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: getStructuredData({
            product,
            reviews,
            texts,
            pagePathOverride,
            hideReviews,
            hideOffers,
          }),
        }}
      ></script>
    </Head>
  );
};

function getStructuredData({
  product,
  reviews,
  texts,
  pagePathOverride,
  hideReviews,
  hideOffers,
}: {
  product: Product;
  reviews: Review[];
  texts: InfoText[];
  pagePathOverride?: string;
  hideReviews?: boolean;
  hideOffers?: boolean;
}) {
  const {
    media: { mainImage: { url: imageUrl = null } } = {
      mainImage: { url: null },
      images: null,
    },
  } = product;

  const { website, organization } = config;
  const faqsData = faqs({ product, texts, pagePathOverride });

  return JSON.stringify(
    {
      '@context': 'https://schema.org',
      '@graph': [
        // include type, name, and id only
        (({ '@type': type, name, '@id': id }) => ({ '@type': type, name, '@id': id }))(
          organization
        ),
        filterObjectByKey(website, ['potentialAction']),
        imageUrl &&
          imageObject({
            image: {
              url: imageUrl,
            },
          }),
        breadcrumbs({ ...product, pagePathOverride }),
        itemPage({ ...product, pagePathOverride, hideFaqs: !faqsData }),
        productData({ product, reviews, pagePathOverride, hideReviews, hideOffers }),
        faqsData,
      ],
    },
    // ignore null/empty properties
    (k, v) => {
      if (Array.isArray(v)) {
        if (!v.length || !v) return undefined;
        return v.filter((val) => val);
      }

      return v ?? undefined;
    }
  );
}

export default StructuredData;
